import type { ReactNode } from 'react';
import { cn } from '@/utils/classnames';

export type StatusBadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const VARIANT_STYLES: Record<StatusBadgeVariant, { bg: string; text: string; dot: string }> = {
  success: { bg: 'bg-[#E3FCEF]', text: 'text-[#006644]', dot: 'bg-[#006644]' },
  warning: { bg: 'bg-[#FFF7E0]', text: 'text-[#8B5A00]', dot: 'bg-[#C77800]' },
  danger: { bg: 'bg-error-container', text: 'text-on-error-container', dot: 'bg-error' },
  info: { bg: 'bg-surface-container-high', text: 'text-primary', dot: 'bg-primary' },
  neutral: { bg: 'bg-surface-container', text: 'text-on-surface', dot: 'bg-on-surface-variant' },
};

export interface StatusBadgeProps {
  variant?: StatusBadgeVariant;
  /** Show a leading colored dot. */
  dot?: boolean;
  className?: string;
  children: ReactNode;
}

export function StatusBadge({ variant = 'neutral', dot = false, className, children }: StatusBadgeProps) {
  const style = VARIANT_STYLES[variant];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-unit rounded px-stack-sm py-unit',
        'font-label-caps text-label-caps uppercase',
        style.bg,
        style.text,
        className,
      )}
    >
      {dot ? <span className={cn('h-1.5 w-1.5 rounded-full', style.dot)} aria-hidden /> : null}
      {children}
    </span>
  );
}
